<script setup>
import { ref, onMounted } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['select', 'custom']);

const { t } = useI18n();
const store = useStore();

const templates = ref([]);
const isLoading = ref(true);

const ICON_MAP = {
  search: 'i-lucide-search',
  cloud: 'i-lucide-cloud',
  webhook: 'i-lucide-webhook',
  package: 'i-lucide-package',
  ticket: 'i-lucide-ticket',
  mail: 'i-lucide-mail',
  'shopping-cart': 'i-lucide-shopping-cart',
  table: 'i-lucide-table',
};

onMounted(async () => {
  try {
    const data = await store.dispatch('captainCustomTools/fetchTemplates');
    templates.value = data || [];
  } catch {
    templates.value = [];
  } finally {
    isLoading.value = false;
  }
});

const handleSelect = template => emit('select', template);
const handleCustom = () => emit('custom');
</script>

<template>
  <div class="flex flex-col gap-4">
    <p class="text-sm text-n-slate-11">
      {{ t('CAPTAIN.CUSTOM_TOOLS.TEMPLATE.SELECT_DESCRIPTION') }}
    </p>

    <!-- Loading state -->
    <div
      v-if="isLoading"
      class="flex items-center justify-center py-8 text-n-slate-11"
    >
      <span class="i-lucide-loader-2 animate-spin mr-2" />
      {{ t('CAPTAIN.CUSTOM_TOOLS.TEMPLATE.LOADING') }}
    </div>

    <!-- Template Grid -->
    <div v-else class="grid grid-cols-2 gap-3">
      <button
        v-for="template in templates"
        :key="template.template_id"
        class="flex flex-col items-start gap-2 p-4 rounded-lg border border-n-weak bg-n-alpha-1 hover:bg-n-alpha-3 hover:border-n-brand cursor-pointer transition-all text-left"
        @click="handleSelect(template)"
      >
        <span
          class="text-lg"
          :class="ICON_MAP[template.icon] || 'i-lucide-wrench'"
        />
        <span class="text-sm font-semibold text-n-slate-12">
          {{ template.title }}
        </span>
        <span class="text-xs text-n-slate-11 line-clamp-2">
          {{ template.description }}
        </span>
      </button>
    </div>

    <!-- Custom Tool Option -->
    <div class="border-t border-n-weak pt-4">
      <Button
        type="button"
        variant="faded"
        color="slate"
        icon="i-lucide-plus"
        :label="t('CAPTAIN.CUSTOM_TOOLS.TEMPLATE.CREATE_CUSTOM')"
        class="w-full bg-n-alpha-2 text-n-blue-text hover:bg-n-alpha-3"
        @click="handleCustom"
      />
    </div>
  </div>
</template>
